import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, User, Tag, BookOpen, Calendar } from 'lucide-react';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  buying_guide: 'Buying Guide',
  selling_guide: 'Selling Guide',
  renting_guide: 'Renting Guide',
  market_trends: 'Market Trends',
  city_spotlight: 'City Spotlight',
  home_loans: 'Home Loans'
};

const CATEGORY_COLORS = {
  buying_guide: 'bg-blue-50 text-blue-700',
  selling_guide: 'bg-emerald-50 text-emerald-700',
  renting_guide: 'bg-amber-50 text-amber-700',
  market_trends: 'bg-purple-50 text-purple-700',
  city_spotlight: 'bg-rose-50 text-rose-700',
  home_loans: 'bg-cyan-50 text-cyan-700'
};

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${API}/blog/${id}`);
        setPost(res.data);
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id]);

  const estimateReadTime = (content) => {
    const words = content?.split(/\s+/).length || 0;
    return Math.max(1, Math.ceil(words / 200));
  };

  const renderContent = (content) => {
    if (!content) return null;
    return content.split('\n').map((paragraph, i) => {
      const trimmed = paragraph.trim();
      if (!trimmed) return null;
      // Detect numbered headings like "1. Title" or "10. Title"
      const numberedHeading = trimmed.match(/^(\d+)\.\s+(.+)/);
      if (numberedHeading) {
        return (
          <h3 key={i} className="text-lg font-semibold text-slate-900 mt-8 mb-3 flex gap-3">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">
              {numberedHeading[1]}
            </span>
            <span className="pt-0.5">{numberedHeading[2]}</span>
          </h3>
        );
      }
      // Bold text markers **text**
      const parts = trimmed.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className="text-slate-700 leading-relaxed mb-4">
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-16">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white pt-16">
        <Navbar />
        <div className="text-center py-32">
          <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-700 mb-2">Article not found</h2>
          <button onClick={() => navigate('/blog')} className="text-blue-600 font-medium hover:underline mt-2">
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-16">
      <Navbar />

      {/* Back Button */}
      <div className="px-6 md:px-12 lg:px-24 pt-6">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors text-sm font-medium"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </button>
        </div>
      </div>

      {/* Article Header */}
      <article className="px-6 md:px-12 lg:px-24 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className={`text-xs font-medium px-3 py-1 rounded-full ${CATEGORY_COLORS[post.category] || 'bg-slate-100 text-slate-700'}`}>
              {CATEGORY_LABELS[post.category] || post.category}
            </span>
            <span className="text-sm text-slate-500 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {estimateReadTime(post.content)} min read
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 leading-tight" data-testid="blog-post-title">
            {post.title}
          </h1>

          <div className="flex items-center gap-4 pb-8 border-b border-slate-100">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
              {post.author?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div>
              <p className="font-medium text-slate-900 text-sm">{post.author}</p>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(post.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>

          {/* Cover Image */}
          {post.cover_image && (
            <div className="my-8 rounded-xl overflow-hidden">
              <img src={post.cover_image} alt={post.title} className="w-full h-auto max-h-96 object-cover" />
            </div>
          )}

          {/* Content */}
          <div className="py-8 prose-slate" data-testid="blog-post-content">
            {renderContent(post.content)}
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap pt-6 border-t border-slate-100">
              <Tag className="w-4 h-4 text-slate-400" />
              {post.tags.map(tag => (
                <span key={tag} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </article>
    </div>
  );
};

export default BlogDetail;
